import React, { useState } from 'react';
import { ArrowRight, Clock, Shield, Star, Phone } from 'lucide-react';
import { heroSection, companyInfo } from '../data/mock';
import { BookingCalendar } from './BookingCalendar';

export const Hero = () => {
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
    >
      {/* Background - Visite virtuelle 360° automatique */}
      <div className="absolute inset-0 z-0">
        {/* Visite virtuelle 360° Panoraven - TEMPORAIREMENT DÉSACTIVÉ (service externe en panne 503) */}
        {/* <iframe
          src="https://panoraven.com/fr/embed/Ci69iVa7Vy?autorotate=true&autorotateSpeed=0.5&hideUI=true"
          className="w-full h-full"
          style={{ border: 0 }}
          allowFullScreen
          allow="accelerometer; magnetometer; gyroscope; xr-spatial-tracking"
          title="Visite virtuelle 360° PHONELAB"
        /> */}
        
        {/* Image de fond temporaire (masquée quand un thème est actif) */}
        <div className="w-full h-full bg-gradient-to-br from-blue-900 via-purple-900 to-indigo-900 hero-bg-layer"></div>
        
        {/* Overlay pour assurer la lisibilité du texte */}
        <div className="absolute inset-0 bg-gradient-to-br from-black/60 via-black/40 to-black/60 pointer-events-none"></div>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
        <div className="animate-fade-in-up">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-white/10 backdrop-blur-md border border-white/20 rounded-full px-6 py-2 mb-8">
            <Star className="text-yellow-400" size={20} fill="currentColor" />
            <span className="text-white font-medium">10 ans d'expérience • +5000 réparations</span>
          </div>

          {/* Main Title — H1 unique : activité principale + zone géographique */}
          <h1 className="text-4xl md:text-6xl lg:text-7xl font-bold text-white mb-6 leading-tight">
            {heroSection.title}
          </h1>

          {/* Subtitle — balise p, pas H2 pour préserver la hiérarchie unique */}
          <p className="text-2xl md:text-3xl font-semibold text-gray-300 mb-6">
            {heroSection.subtitle}
          </p>

          <p className="text-xl md:text-2xl text-gray-400 mb-12 max-w-3xl mx-auto">
            {heroSection.description}
          </p>

          {/* CTA Buttons avec animations */}
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-8">
            {/* BOUTON RDV PRIORITAIRE */}
            <button
              onClick={() => setIsBookingOpen(true)}
              className="group relative flex items-center justify-center space-x-2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl hover:shadow-orange-500/50 overflow-hidden"
            >
              <span className="absolute inset-0 bg-white/20 animate-ping"></span>
              <span className="relative z-10 flex items-center space-x-2">
                <span>🔥</span>
                <span>RDV Prioritaire</span>
              </span>
            </button>
            
            {/* BOUTON DEMANDER UN DEVIS */}
            <button
              onClick={() => scrollToSection('quote')}
              className="group relative flex items-center justify-center space-x-2 bg-white text-black px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl"
            >
              <span className="relative z-10">📝 Demander un Devis</span>
            </button>
            
            {/* BOUTON DIAGNOSTIC GRATUIT */}
            <button
              onClick={() => scrollToSection('sell')}
              className="group relative flex items-center space-x-2 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl hover:shadow-green-500/50"
            >
              {/* Badge FREE */}
              <span className="absolute -top-3 -right-3 bg-yellow-400 text-black text-sm font-black px-3 py-1.5 rounded-full shadow-lg z-20 animate-bounce border-2 border-white">
                FREE
              </span>
              <span className="relative z-10">🔍 Diagnostic Gratuit</span>
            </button>
            
            {/* BOUTON TÉLÉPHONE */}
            <a
              href="tel:+33426008210"
              className="group relative flex items-center justify-center space-x-2 bg-blue-600 border-2 border-blue-600 text-white px-8 py-4 rounded-full font-bold text-lg transition-all duration-300 transform hover:scale-110 shadow-2xl hover:shadow-blue-500/50"
            >
              <Phone className="relative z-10" size={20} />
              <span className="relative z-10">☎️ 04 26 00 82 10</span>
            </a>
          </div>

          {/* Trust Indicators */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 transform hover:scale-105 transition-transform duration-300">
              <Clock className="text-blue-300 mb-3 mx-auto" size={40} />
              <h2 className="text-white font-bold text-lg mb-2">Intervention Rapide</h2>
              <p className="text-blue-200 text-sm">Réparation express dès 15 min sous vos yeux</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 transform hover:scale-105 transition-transform duration-300">
              <Shield className="text-green-300 mb-3 mx-auto" size={40} />
              <h2 className="text-white font-bold text-lg mb-2">Garantie jusqu'à 2 ans</h2>
              <p className="text-blue-200 text-sm">2 ans appareils neufs · 1 an pièces d'origine</p>
            </div>
            <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 transform hover:scale-105 transition-transform duration-300">
              <Star className="text-yellow-300 mb-3 mx-auto" size={40} fill="currentColor" />
              <h2 className="text-white font-bold text-lg mb-2">Rapport Qualité-Prix</h2>
              <p className="text-blue-200 text-sm">Les meilleurs prix du marché, stock immédiat</p>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
        <div className="w-6 h-10 border-2 border-white rounded-full flex justify-center">
          <div className="w-1.5 h-3 bg-white rounded-full mt-2 animate-pulse"></div>
        </div>
      </div>
      
      {/* Booking Calendar Modal */}
      <BookingCalendar
        isOpen={isBookingOpen}
        onClose={() => setIsBookingOpen(false)}
      />
    </section>
  );
};
